import {
  Edit,
  Image as ImageIcon,
  OverflowMenuVertical,
  TrashCan,
  Upload,
} from '@carbon/icons-react'
import { Box, IconButton, Image, Input, Menu, Portal, Text, VStack } from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { useColorModeValue } from '~/color-mode'

interface CustomerCardProps {
  customerName: string
  customerLogoUrl: string | null
  customerLogoFile: File | null
  onCustomerNameChange: (name: string) => void
  onCustomerLogoChange: (file: File | null, url: string | null) => void
}

function CustomerCard({
  customerName,
  customerLogoUrl,
  customerLogoFile,
  onCustomerNameChange,
  onCustomerLogoChange,
}: CustomerCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState(customerName)
  const uploadIconColor = useColorModeValue('#777672', '#BBB9B5')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Get the display URL - either from file preview or existing URL
  const displayLogoUrl = customerLogoFile ? URL.createObjectURL(customerLogoFile) : customerLogoUrl

  const handleNameSubmit = () => {
    onCustomerNameChange(editedName)
    setIsEditing(false)
  }

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit()
    } else if (e.key === 'Escape') {
      setEditedName(customerName)
      setIsEditing(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      onCustomerLogoChange(file, null)
    }
  }

  const handleRemoveLogo = () => {
    onCustomerLogoChange(null, null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Don't show card if there's no content
  const hasContent = displayLogoUrl || customerName
  if (!hasContent) {
    return (
      <Box
        position="absolute"
        top={2}
        right={2}
        zIndex={1000}
        bg="bg.panel"
        borderRadius="md"
        boxShadow="md"
        p={3}
        minW="120px"
        border="2px dashed"
        borderColor="gray.300"
        _hover={{ borderColor: 'gray.400' }}
        cursor="pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <VStack gap={1}>
          <Upload size={24} color={uploadIconColor} />
          <Text fontSize="xs" color="content.secondary" textAlign="center">
            Add customer branding
          </Text>
        </VStack>
      </Box>
    )
  }

  return (
    <Box
      position="absolute"
      top={2}
      right={2}
      zIndex={1000}
      bg="bg.panel"
      borderRadius="md"
      boxShadow="md"
      p={3}
      minW="120px"
      maxW="200px"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Menu button in top right */}
      <Box position="absolute" top={1} right={1}>
        <Menu.Root>
          <Menu.Trigger asChild>
            <IconButton
              aria-label="Options"
              size="xs"
              variant="ghost"
              color="content.secondary"
              _hover={{ bg: 'gray.100', color: 'gray.700' }}
              borderRadius="full"
            >
              <OverflowMenuVertical size={16} />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content minW="140px" zIndex={1001}>
                <Menu.Item value="change-logo" onClick={() => fileInputRef.current?.click()}>
                  <ImageIcon size={16} />
                  <Text ml={2}>{displayLogoUrl ? 'Change logo' : 'Add logo'}</Text>
                </Menu.Item>
                <Menu.Item
                  value="edit-name"
                  onClick={() => {
                    setEditedName(customerName)
                    setIsEditing(true)
                  }}
                >
                  <Edit size={16} />
                  <Text ml={2}>Edit name</Text>
                </Menu.Item>
                {displayLogoUrl && (
                  <Menu.Item value="remove-logo" onClick={handleRemoveLogo} color="red.500">
                    <TrashCan size={16} />
                    <Text ml={2}>Remove logo</Text>
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Box>

      <VStack gap={2} align="center" pt={4}>
        {/* Logo */}
        {displayLogoUrl ? (
          <Image
            src={displayLogoUrl}
            alt="Customer Logo"
            maxH="60px"
            maxW="100%"
            objectFit="contain"
            mx="auto"
          />
        ) : (
          <Box
            w="full"
            h="60px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            border="2px dashed"
            borderColor="gray.300"
            borderRadius="md"
            cursor="pointer"
            _hover={{ borderColor: 'gray.400' }}
            onClick={() => fileInputRef.current?.click()}
          >
            <VStack gap={0}>
              <Upload size={20} color={uploadIconColor} />
              <Text fontSize="xs" color="content.secondary">
                Add logo
              </Text>
            </VStack>
          </Box>
        )}

        {/* Customer Name */}
        {isEditing ? (
          <Input
            size="sm"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={handleNameKeyDown}
            textAlign="center"
            autoFocus
            placeholder="Customer name"
          />
        ) : (
          <Box
            w="full"
            cursor="pointer"
            onClick={() => {
              setEditedName(customerName)
              setIsEditing(true)
            }}
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
            px={2}
            py={1}
          >
            {customerName ? (
              <Text fontSize="sm" fontWeight="medium" textAlign="center" lineClamp={2}>
                {customerName}
              </Text>
            ) : (
              <Text fontSize="xs" color="content.secondary" textAlign="center">
                Click to add name
              </Text>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export default CustomerCard
